import { ApolloClient, createHttpLink, from, InMemoryCache } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { spiffCoreConfiguration } from "../Configuration";
import { fetch } from "../util/crossplatform";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";

/**
 * The different types of authentication for server calls.
 */
interface AuthenticationObject {
    activeIntegration?: string;
    applicationKey?: string;
    bearer?: string;
    customerToken?: string;
    partnerId?: string;
}

let localCustomerToken: string | undefined = undefined;
export const setLocalCustomerToken = (customerToken: string) => {
    localCustomerToken = customerToken;
};

let applicationKey: string | undefined = undefined;
export const setApplicationKey = (key: string) => {
    applicationKey = key;
};

export const getApplicationKey = () => {
    return applicationKey;
};

let bearerAuthenticationToken: string | undefined = undefined;
export const setBearerAuthenticationToken = (token: string) => {
    bearerAuthenticationToken = token;
};

/**
 *  Request authentication from the parent window.
 * @returns A promise resolving to an authentication object containing
 *          the required credentials.
 */
const getHubAuthentication = (): Promise<AuthenticationObject> => {
    return new Promise((resolve) => {
        const hubUrl = spiffCoreConfiguration.getHubUrl();
        const onAuthenticationReceived = (event: MessageEvent<AuthenticationObject>) => {
            if (event.origin !== hubUrl) return;
            window.removeEventListener("message", onAuthenticationReceived);
            resolve(event.data);
        };
        if (window.parent !== window) {
            window.addEventListener("message", onAuthenticationReceived, false);
            window.parent.postMessage("ready", hubUrl);
        }
    });
};

/**
 * Retrieve stored authentication values for server calls.
 */
const getServerAuth = async (): Promise<AuthenticationObject> => {
    const localAuthObj = {
        applicationKey: applicationKey,
        customerToken: localCustomerToken,
        bearer: bearerAuthenticationToken,
    };

    // When in an internal iframe, get authentication from the parent window.
    if (
        typeof window !== "undefined" &&
        (window.location.href.includes("/workflows/product/") || window.location.href.includes("hub=true"))
    ) {
        return { ...(await getHubAuthentication()), ...localAuthObj };
    } else {
        return localAuthObj;
    }
};

async function followRedirectFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const serverUrl = spiffCoreConfiguration.getServerUrl();
    const moonlightUrl = serverUrl.includes(".aumelbdev.spiffcommerce.com")
        ? "https://moonlight.aumelbdev.spiffcommerce.com"
        : serverUrl.includes(".us.spiffcommerce.com")
        ? "https://moonlight.us.spiffcommerce.com"
        : "https://moonlight.au.spiffcommerce.com";

    let result = await fetch(input, { ...init, redirect: "manual" });
    if (result.type === "opaqueredirect") {
        if (typeof input !== "string" && "url" in input) {
            result = await fetch({ ...input, url: `${moonlightUrl}/graphql` }, init);
        } else {
            result = await fetch(`${moonlightUrl}/graphql`, init);
        }
    }
    return result;
}

class GraphQlManager {
    private shadowGraphqlClient: ApolloClient<any>;

    constructor() {
        this.shadowGraphqlClient = this.constructShadowGraphqlClient();
        spiffCoreConfiguration.addServerUrlCallback(() => {
            this.shadowGraphqlClient = this.constructShadowGraphqlClient();
        });
    }

    getShadowGraphqlClient() {
        return this.shadowGraphqlClient;
    }

    private constructShadowGraphqlClient() {
        const httpLink = createHttpLink({
            uri: `${spiffCoreConfiguration.getServerUrl()}/graphql`,
            fetch: followRedirectFetch,
        });
        const authLink = setContext(async (_request, operationContext) => {
            const { headers } = operationContext;
            const newHeaders = headers || {};
            const auth = await getServerAuth();

            // Request context values take precedence over stored values.
            // These are passed on the query and mutate functions.
            const bearer = operationContext.bearer ?? auth.bearer;
            const partnerId = operationContext.partnerId ?? auth.partnerId;
            const activeIntegration = operationContext.activeIntegration ?? auth.activeIntegration;
            const transactionOwnerId = operationContext.transactionOwnerId;
            const customerToken = operationContext.customerToken ?? auth.customerToken;
            const applicationKey = operationContext.applicationKey ?? auth.applicationKey;
            const bundleOwnerId = operationContext.bundleOwnerId;

            /**
             * Sentry is an optional dependency. If it's available, we check if there's an
             * active trace & attach any sentry related headers to the request.
             */
            try {
                if (window && window["__SENTRY__"]) {
                    const hub = window["__SENTRY__"].hub;
                    if (hub) {
                        const traceHeaders = hub.traceHeaders();
                        Object.entries(traceHeaders).forEach(([key, value]) => {
                            newHeaders[key] = value;
                        });
                    }
                }
            } catch (e) {
                // Sentry is not available
            }

            if (bearer) {
                newHeaders["Authorization"] = `Bearer ${bearer}`;
            }
            if (partnerId) {
                newHeaders["partnerId"] = partnerId;
            }
            if (activeIntegration) {
                newHeaders["activeIntegration"] = activeIntegration;
            }
            if (transactionOwnerId) {
                newHeaders["transactionOwnerId"] = transactionOwnerId;
            }
            if (customerToken) {
                newHeaders["customerToken"] = customerToken;
            }
            if (applicationKey) {
                newHeaders["X-Application-Key"] = applicationKey;
            }
            if (bundleOwnerId) {
                newHeaders["bundleOwnerId"] = bundleOwnerId;
            }

            newHeaders["Allow-Moonlight-Redirect"] = "true";

            return {
                headers: newHeaders,
            };
        });

        const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
            const errors = graphQLErrors || [];
            if (networkError) console.log("GraphQL Network error");
            errors.forEach(() => console.log("GraphQL Error: " + operation.operationName));
        });

        const removeTypenameLink = removeTypenameFromVariables();

        return new ApolloClient({
            link: from([removeTypenameLink, errorLink, authLink, httpLink]),
            cache: new InMemoryCache(),
            name: "Core",
        });
    }
}

const graphQlManager = new GraphQlManager();
export { graphQlManager };
